import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, req } from '@/lib/api/client';
import { WS_URL } from '@/config/constants';
import { usePresence, type Viewer } from '@/stores/presence';
import { toast } from '@/stores/ui';
import { saveUrl } from '@/lib/api/jobs';
import { uploadManager } from '@/features/upload/UploadManager';
import { RealtimeClient } from './RealtimeClient';
import { applyEvent, invalidateRoom } from './apply';

const Ctx = createContext<RealtimeClient | null>(null);

/** Socket URL from a minted connect token: the token travels in the query string for one 60 s single use, never the share token itself (REVIEW 31). */
function socketUrl(t: { token: string; ws_url?: string }): string {
  return `${t.ws_url ?? WS_URL}?token=${encodeURIComponent(t.token)}`;
}

/**
 * Mounted once per authenticated shell (and per share page).
 *
 * Members exchange their session for a single-use token at `POST /auth/ws-token`; share-link guests do the
 * same through the share cookie at `POST /public/share/{token}/ws-token` (scoped to the shared subtree by the
 * gateway). Guest-upload principals have no realtime surface and get no socket.
 */
export function RealtimeProvider({ children, mode = 'member', token }: { children: ReactNode; mode?: 'member' | 'share' | 'guest'; token?: string }) {
  const qc = useQueryClient();
  const client = useMemo(() => {
    const onReplayGap = (room: string) => invalidateRoom(qc, room);
    if (mode === 'member') return new RealtimeClient({ getUrl: async () => socketUrl(await req(api.POST('/auth/ws-token'))), onReplayGap });
    if (mode === 'share' && token)
      return new RealtimeClient({
        getUrl: async () => socketUrl(await req(api.POST('/public/share/{token}/ws-token', { params: { path: { token } } }))),
        onReplayGap,
      });
    return null;
  }, [qc, mode, token]);
  useEffect(() => {
    if (!client) return;
    const off = client.on('*', (env) =>
      applyEvent(qc, env, {
        onVersionState: (a, v, s, p, card, err) => uploadManager.onVersionState(a, v, s, p, card, err),
        onVersionActivated: (a, card, info) => uploadManager.onVersionActivated(a, card, info),
        clientUploadFor: (a) => uploadManager.clientUploadFor(a),
        onPresence: (room, viewers) => usePresence.getState().setRoom(room, viewers as Viewer[]),
        // not part of the WS contract (zip jobs are polled, see features/grid/zipJob.ts); honoured if a server ever sends it
        onDownloadReady: (d) => toast({ kind: 'success', title: `Zip ready (${d.asset_count} assets)`, action: { label: 'Download', onClick: () => saveUrl(d.url, 'assets.zip') }, durationMs: 60_000 }),
        openAssetId: () => (window as unknown as { __openAsset?: string | null }).__openAsset ?? null,
      }),
    );
    (window as unknown as { __rt?: RealtimeClient }).__rt = client;
    return () => {
      off();
      client.close();
    };
  }, [client, qc]);
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeClient | null {
  return useContext(Ctx);
}

/** Lets the overlay mark which asset is open (drives `asset.updated` invalidation). */
export function useMarkOpenAsset(assetId: string | null) {
  useEffect(() => {
    const w = window as unknown as { __openAsset?: string | null };
    w.__openAsset = assetId;
    return () => {
      if (w.__openAsset === assetId) w.__openAsset = null;
    };
  }, [assetId]);
}
